// CSR-857: shared section header bar used by sections inside the
// Insurance Company detail page (Policies, Child Companies, Contacts).
// Renders an icon + title + an optional right-aligned count pill.
//
// Extracted from InsuranceCompanyDetail.tsx (CSR-856) so the new
// Contacts section can reuse it without dragging the page into its
// import graph.

import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface DetailSectionHeaderProps {
  icon: ReactNode;
  title: string;
  count?: number;
  /** Optional right-slot for action buttons (e.g. "+ Add Contact"). */
  action?: ReactNode;
  /** Test id applied to the count pill. */
  testid?: string;
}

export function DetailSectionHeader({
  icon,
  title,
  count,
  action,
  testid,
}: DetailSectionHeaderProps) {
  // When there's no `action`, the count pill takes the right-slot so the
  // historical PoliciesSection / ChildCompaniesSection layout stays
  // pixel-stable. When an `action` is present, the pill sits next to the
  // title and the action slides to the right.
  return (
    <div className="px-6 py-4 border-b border-border bg-muted/30 flex items-center gap-2">
      {icon}
      <h3 className="font-semibold font-heading text-foreground">{title}</h3>
      {typeof count === "number" && (
        <span
          className={cn(
            "text-xs font-medium text-muted-foreground bg-muted px-2 py-0.5 rounded-full",
            !action && "ml-auto",
          )}
          data-testid={testid}
        >
          {count} total
        </span>
      )}
      {action && <div className="ml-auto">{action}</div>}
    </div>
  );
}
